/**
 * 매장 정보 세팅(/setup/) 로그인.
 *
 * 2단계다. 아이디를 보내면 매장 사이트가 등록된 이메일로 인증 코드를 보내고,
 * 그 코드를 다시 보내면 세션이 생긴다. 비밀번호는 주고받지 않는다.
 */

import { busy } from './busyButton';
import initCodeInput from './codeInput';
import initScrollSpyNav from './scrollSpyNav';

const postAjax = async (root, action, data) => {
  // 임시: 화면 주소에 ?mode=test 가 있으면 서버도 Turnstile 테스트 키로 검증하게 함께 보낸다
  const mode =
    new URLSearchParams(window.location.search).get('mode') === 'test' ? { mode: 'test' } : {};
  const body = new URLSearchParams({ action, nonce: root.dataset.nonce, ...mode, ...data });
  const response = await fetch(root.dataset.ajaxUrl, {
    method: 'POST',
    body,
    credentials: 'same-origin',
  });
  const json = await response.json().catch(() => null);

  if (!json || !json.success) {
    // 통합 관리 계정이 매장을 다시 골라야 한다 — 서버가 준 선택 화면으로
    if (json?.data?.reselect && json.data.redirect) {
      window.location.assign(json.data.redirect);
    }

    throw new Error(json?.data?.message || `요청 실패 (HTTP ${response.status})`);
  }

  return json.data;
};

// 상태 메시지는 공통 메시지(.notice)에 담는다 — 글은 .notice__text 에, 색은 --success/--error 로. 빈 메시지면 감춘다.
const setNotice = (element, message, state) => {
  element.querySelector('.notice__text').textContent = message;
  element.classList.remove('notice--success', 'notice--error', 'notice--warning');

  if (['success', 'error', 'warning'].includes(state)) {
    element.classList.add(`notice--${state}`);
  }

  element.hidden = '' === message;
};

const resetCaptcha = () => {
  try {
    window.turnstile?.reset();
  } catch {
    // 위젯이 없으면 무시
  }
};

const InitShopSetup = () => {
  const root = document.querySelector('[data-qrshop-setup]');

  if (!root) {
    return;
  }

  const form = root.querySelector('form');
  const status = root.querySelector('[data-setup-status]');
  const codeInput = initCodeInput(root.querySelector('[data-setup-code]'));

  // 로그인 폼이 아니면(이미 로그인 상태) 아무것도 하지 않는다
  if (!form || !codeInput) {
    return;
  }

  // 인증 코드 칸(카드 안)과 로그인 버튼(카드 아래)이 같은 단계라 verify 는 여럿이다
  const stepRequest = root.querySelectorAll('[data-setup-step="request"]');
  const stepVerify = root.querySelectorAll('[data-setup-step="verify"]');
  const captcha = root.querySelector('[data-setup-captcha]');
  const typeInputs = root.querySelectorAll('[data-setup-type]');
  const shopType = () => root.querySelector('[data-setup-type]:checked')?.value || '';
  const usernameInput = root.querySelector('[data-setup-username]');
  const sendButton = root.querySelector('[data-setup-send]');
  const loginButton = root.querySelector('[data-setup-login]');
  const restartButton = root.querySelector('[data-setup-restart]');

  // 좌측 섹션 메뉴 (모바일: 상단 칩) — /register/ 와 같은 방식
  const navEl = root.querySelector('[data-setup-login-nav]');
  if (navEl) {
    initScrollSpyNav(
      navEl,
      Array.from(root.querySelectorAll('[data-nav-label]')).map((section) => ({
        el: section,
        label: section.dataset.navLabel,
      }))
    );
  }

  let step = 'request';

  const setStatus = (message, state) => {
    setNotice(status, message, state);
  };

  const showStep = (next) => {
    step = next;
    const verifying = next === 'verify';

    stepRequest.forEach((element) => {
      element.hidden = verifying;
    });
    stepVerify.forEach((element) => {
      element.hidden = !verifying;
    });
    captcha.hidden = verifying;

    // 코드를 받은 뒤에는 대상과 아이디를 바꿀 수 없게 잠근다
    typeInputs.forEach((input) => {
      input.disabled = verifying;
    });
    usernameInput.disabled = verifying;

    if (verifying) {
      codeInput.clear();
      codeInput.focus();
    } else {
      // 캡차 토큰은 1회용이라 되돌아올 때 새로 받아야 한다
      resetCaptcha();
      usernameInput.focus();
    }
  };

  restartButton?.addEventListener('click', () => {
    setStatus('', '');
    showStep('request');
  });

  const requestCode = async () => {
    const username = usernameInput.value.trim();

    if (!username) {
      setStatus('아이디를 입력해 주세요.', 'error');
      return;
    }

    const release = busy(sendButton, '보내는 중…');
    setStatus('인증 코드를 보내는 중입니다...', 'info');

    try {
      const data = await postAjax(root, 'qrshop_setup_login_request', {
        shop_type: shopType(),
        username,
        turnstile: document.querySelector('[name="cf-turnstile-response"]')?.value || '',
      });

      showStep('verify');
      setStatus(data.message || '등록된 이메일로 인증 코드를 보냈습니다.', 'success');
    } catch (error) {
      resetCaptcha();
      setStatus(error.message, 'error');
    } finally {
      release();
    }
  };

  const verifyCode = async () => {
    // Enter 로 폼 제출과 keydown 확인이 겹쳐 다시 불릴 수 있다 — 진행 중이면 무시
    if ('true' === loginButton.getAttribute('aria-busy')) {
      return;
    }

    const code = codeInput.value();

    if (code.length !== 6) {
      setStatus('인증 코드 6자리를 입력해 주세요.', 'error');
      return;
    }

    const release = busy(loginButton, '확인 중…');
    setStatus('확인 중입니다...', 'info');

    try {
      const data = await postAjax(root, 'qrshop_setup_login_verify', {
        shop_type: shopType(),
        username: usernameInput.value.trim(),
        code,
      });

      setStatus('로그인되었습니다. 이동 중...', 'success');

      // 통합 관리 계정(매장 둘 이상)은 매장 선택 화면으로, 그 밖에는 이 화면을 로그인 상태로 다시 그린다
      if (data?.redirect) {
        window.location.assign(data.redirect);
      } else {
        window.location.reload();
      }
    } catch (error) {
      setStatus(error.message, 'error');
      release();
      codeInput.clear();
      codeInput.focus();
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (step === 'request') {
      requestCode();
    } else {
      verifyCode();
    }
  });
};

export default InitShopSetup;
